import {HttpClient, HttpErrorResponse}                                          from '@angular/common/http';
import {inject, Injectable, OnDestroy}                                          from '@angular/core';
import {BaseService}                                                            from '@src/app/shared/services/base.service';
import {BillingPolicyContract}                                                   from '@src/app/core/models/billing-policy-contract.model';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {StorageService}                                                         from './storage.service';

@Injectable({providedIn: 'root'})
export class BillingPolicyService extends BaseService implements OnDestroy
{
    private readonly http = inject(HttpClient);
    private readonly storage = inject(StorageService);
    private readonly state = new BehaviorSubject<BillingPolicyContract | null>(null);
    private readonly refreshes = new Map<string, Promise<BillingPolicyContract | null>>();
    private currentTenantId: string | null = null;
    private expiryTimeoutId?: number;
    private testPlan: string | null = null;
    private testContext: string | null = null;
    private generation = 0;

    public get testPlanHeader(): string | null
    {
        if (this.testPlan && this.testContext !== this.sessionContext()) {
            this.clearCurrent();
        }
        return this.testPlan;
    }

    public async switchTestPlan(plan: string | null): Promise<void>
    {
        const tenantId = this.currentTenantId;
        if (!tenantId) throw new Error('Select a tenant first.');
        this.generation++;
        this.refreshes.clear();
        this.storage.remove(this.cacheKey(tenantId));
        this.testPlan = null;
        this.testContext = null;
        this.publish(null);
        const generation = this.generation;
        const context = this.sessionContext();
        if (plan === null) {
            await this.refresh(tenantId);
            return;
        }
        try {
            const policy = await firstValueFrom(this.http.post<BillingPolicyContract>(`${this.apiUrl}/payments/test-plan`, {}, {headers: {'X-Test-Plan': plan}}));
            if (generation !== this.generation || context !== this.sessionContext()) return;
            if (String(policy.tenant_id) !== tenantId || policy.test_plan !== plan || policy.status !== 'resolved') {
                throw new Error('The test plan could not be resolved.');
            }
            this.testPlan = plan;
            this.testContext = context;
            this.publish(policy);
        } catch (error) {
            if (generation === this.generation) await this.refresh(tenantId);
            throw error;
        }
    }

    private sessionContext(): string
    {
        return `${this.storage.read('accessToken')}|${this.storage.read('tenant')}`;
    }

    public readonly policy$ = this.state.asObservable();

    public async initialize(tenantId: string | number | null | undefined, forceRefresh = false): Promise<BillingPolicyContract | null>
    {
        if (tenantId === null || tenantId === undefined || tenantId === '') {
            this.clearCurrent();
            return null;
        }

        void this.testPlanHeader;
        const id = String(tenantId);
        if (this.currentTenantId !== id) {
            this.clearCurrent();
            this.currentTenantId = id;
            this.publish(this.readCached(id));
        }

        const current = this.state.value;
        if (!forceRefresh && current?.status === 'resolved' && this.isFresh(current)) {
            return current;
        }

        if (forceRefresh) {
            this.storage.remove(this.cacheKey(id));
            this.publish(null);
        }
        return this.refresh(id);
    }

    public async refresh(tenantId: string | number): Promise<BillingPolicyContract | null>
    {
        const id = String(tenantId);
        const pending = this.refreshes.get(id);
        if (pending) {
            return pending;
        }

        const generation = this.generation;
        const context = this.sessionContext();
        const refresh = firstValueFrom(this.http.get<BillingPolicyContract>(`${this.apiUrl}/payments/policy`))
            .then(policy => {
                if (generation !== this.generation || context !== this.sessionContext()) return null;
                if (!policy?.tenant_id || String(policy.tenant_id) !== id) {
                    throw new Error('Billing policy tenant mismatch.');
                }

                this.cache(id, policy);
                if (this.currentTenantId === id) {
                    this.publish(policy);
                }

                return policy;
            })
            .catch((error: unknown) => {
                if (generation !== this.generation || context !== this.sessionContext()) return null;
                const rejectedPolicy = error instanceof HttpErrorResponse ? error.error as BillingPolicyContract : null;
                if (rejectedPolicy?.tenant_id && String(rejectedPolicy.tenant_id) === id) {
                    this.cache(id, rejectedPolicy);

                    if (this.currentTenantId === id) {
                        this.publish(rejectedPolicy);
                    }

                    return rejectedPolicy;
                }

                if (this.currentTenantId === id) {
                    this.publish(null);
                }

                return null;
            })
            .finally(() => { if (generation === this.generation) this.refreshes.delete(id); });

        this.refreshes.set(id, refresh);

        return refresh;
    }

    public isFeatureAllowed(feature: string): boolean
    {
        const policy = this.state.value;
        if (!policy || String(policy.tenant_id) !== this.currentTenantId) {
            return false;
        }

        if (policy.status === 'not_enabled') {
            return true;
        }

        if (policy.status !== 'resolved' || !this.isFresh(policy)) {
            return false;
        }

        return policy.entitlements?.[feature] === true;
    }

    public usesCredits(): boolean
    {
        const policy = this.state.value;
        if (!policy || String(policy.tenant_id) !== this.currentTenantId) {
            return true;
        }

        if (policy.status !== 'resolved' || !this.isFresh(policy)) {
            return true;
        }

        return policy.uses_credits !== false;
    }

    public clearCurrent(): void
    {
        this.generation++;
        this.refreshes.clear();
        this.testPlan = null;
        this.testContext = null;
        this.currentTenantId = null;
        this.clearExpiryTimer();
        this.state.next(null);
    }

    public ngOnDestroy(): void
    {
        this.clearCurrent();
    }

    private readCached(tenantId: string): BillingPolicyContract | null
    {
        const raw = this.storage.read(this.cacheKey(tenantId));
        if (!raw) {
            return null;
        }

        try {
            const policy = JSON.parse(raw) as BillingPolicyContract;
            if (!policy || policy.test_plan || String(policy.tenant_id) !== tenantId) {
                return null;
            }

            if (policy.status === 'resolved' && !this.isFresh(policy)) {
                return null;
            }
            return policy;
        } catch {
            return null;
        }
    }

    private cache(tenantId: string, policy: BillingPolicyContract): void
    {
        if (policy.test_plan || this.testPlan) {
            this.storage.remove(this.cacheKey(tenantId));
            return;
        }
        this.storage.write(this.cacheKey(tenantId), JSON.stringify(policy));
    }

    private publish(policy: BillingPolicyContract | null): void
    {
        this.clearExpiryTimer();
        this.state.next(policy);

        if (!policy || policy.status !== 'resolved' || !policy.valid_until) {
            return;
        }

        const expiresAt = Date.parse(policy.valid_until);
        if (!Number.isFinite(expiresAt)) {
            return;
        }

        const delay = expiresAt - Date.now();
        if (delay <= 0) {
            this.state.next(null);
            void this.refreshCurrentTenant();
            return;
        }

        this.expiryTimeoutId = window.setTimeout(() => {
            if (this.state.value !== policy) {
                return;
            }

            this.state.next(null);
            void this.refreshCurrentTenant();
        }, Math.min(delay, 2147483647));
    }

    private async refreshCurrentTenant(): Promise<void>
    {
        if (this.currentTenantId !== null) {
            await this.refresh(this.currentTenantId);
        }
    }

    private isFresh(policy: BillingPolicyContract): boolean
    {
        if (!policy.valid_until) {
            return false;
        }

        const expiresAt = Date.parse(policy.valid_until);
        return Number.isFinite(expiresAt) && expiresAt > Date.now();
    }

    private clearExpiryTimer(): void
    {
        if (this.expiryTimeoutId !== undefined) {
            window.clearTimeout(this.expiryTimeoutId);
            this.expiryTimeoutId = undefined;
        }
    }

    private cacheKey(tenantId: string): string
    {
        return `billingPolicy:${tenantId}`;
    }
}
